import { useHotkeys } from "@mantine/hooks";
import { lazy, Suspense, useCallback, useEffect, useState, type FC } from "react";

import { searchOpenEventName } from "src/routing/searchLauncher";

const Search = lazy(() => import("../Search"));

/**
 * Loads the search UI on the first keyboard shortcut or search-open event.
 *
 * Postconditions: Requests increment a counter observed on later renders. Once requested, the
 * search stays mounted; Suspense renders nothing while its module loads. Effect cleanup removes
 * the window listener. Module-load failures are left to an enclosing error boundary.
 */
const LazySearch: FC = () => {
    const [openRequest, setOpenRequest] = useState(0);

    /** Requests another search opening, including repeated requests after the initial load. */
    const requestOpen = useCallback(() => setOpenRequest(current => current + 1), []);

    useHotkeys([["mod + K", requestOpen]]);

    useEffect(
        () => {
            window.addEventListener(searchOpenEventName, requestOpen);
            return () => window.removeEventListener(searchOpenEventName, requestOpen);
        },
        [requestOpen]
    );

    if (openRequest === 0) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <Search openRequest={openRequest} />
        </Suspense>
    );
};

export default LazySearch;
